import { CommonModule, NgClass } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { PopupModule } from '@progress/kendo-angular-popup';
import { IconComponent } from '@src/app/shared/components';
import { StatusBadgeComponent } from '@src/app/shared/components/status-badge/status-badge.component';
import { DATA_SOURCE_TYPES_DICTIONARY } from '@src/app/shared/enums';
import {
  DataSourceSearchResult,
  FacilitySearchResult,
  FeatureResult,
  SearchResultKey,
  ShipmentSearchResult,
} from '@src/app/shared/models';
import { FormatDatePipe, HighlightPipe } from '@src/app/shared/pipes';
import { ResponsiveService } from '@src/app/shared/services';
import { debounceTime, filter, Subscription } from 'rxjs';
import { SearchService } from '../services/search.service';

@Component({
  selector: 'cs-universal-search',
  imports: [
    CommonModule,
    InputsModule,
    PopupModule,
    FormsModule,
    ReactiveFormsModule,
    IconComponent,
    NgClass,
    LoaderModule,
    FormatDatePipe,
    StatusBadgeComponent,
    HighlightPipe,
    RouterLink
  ],
  templateUrl: './universal-search.component.html'
})
export class UniversalSearchComponent implements OnInit, OnDestroy {
  private searchService = inject(SearchService);
  private responsiveService = inject(ResponsiveService);

  @ViewChild('search', { read: ElementRef }) public searchElement!: ElementRef;
  @ViewChild('popup', { read: ElementRef }) public popup!: ElementRef;

  public searchForm = new FormGroup({
    queryInput: new FormControl('', {
      validators: [Validators.maxLength(256)],
      nonNullable: true,
    }),
  });
  public DATA_SOURCE_TYPES_DICTIONARY = DATA_SOURCE_TYPES_DICTIONARY;
  public showPopup = signal<boolean>(false);
  public loadingStatus = signal<'loading' | 'loading-more' | 'idle'>('idle');
  public tab = signal<SearchResultKey>('shipments');
  public query = signal<QueryParams>({
    q: '',
    page: 0,
  });
  public shipments = signal<FeatureResult<ShipmentSearchResult>>({
    content: [],
    totalElements: 0
  });
  public dataSources = signal<FeatureResult<DataSourceSearchResult>>({
    content: [],
    totalElements: 0
  });
  public facilities = signal<FeatureResult<FacilitySearchResult>>({
    content: [],
    totalElements: 0
  });

  public totalMatches = computed<number>(() => {
    return this.shipments().totalElements + this.dataSources().totalElements + this.facilities().totalElements;
  });
  public isSearchVisible = computed<boolean>(() => {
    return this.responsiveService.isSearchVisible();
  });
  public isMobileView = computed<boolean>(() => {
    return this.responsiveService.isMobile();
  });

  public loadMoreFor = computed<Record<SearchResultKey, boolean>>(() => {
    const shipments = this.shipments();
    const dataSources = this.dataSources();
    const facilities = this.facilities();
    return {
      shipments: this.isLoadMoreVisible(shipments.content, shipments.totalElements),
      dataSources: this.isLoadMoreVisible(dataSources.content, dataSources.totalElements),
      facilities: this.isLoadMoreVisible(facilities.content, facilities.totalElements),
    }
  })

  private isLoadMoreVisible<T>(data: T[], total: number): boolean {
    return data.length < total && data.length < 100
  }

  private searchByTab({
    q,
    tab,
    page,
  }: QueryParams & { tab: SearchResultKey }): void | Subscription {
    this.loadingStatus.set(page === 0 ? 'loading' : 'loading-more');
    switch (tab) {
      case 'shipments':
        return this.searchService.searchShipments({ q, page }).subscribe({
          next: (res) => {
            this.shipments.update(state => ({
              ...state,
              content: page === 0 ? res.content : [...state.content, ...res.content],
            }));
            this.loadingStatus.set('idle');
            this.showPopup.set(true);
          },
          error: (err) => {
            console.error(err);
            this.loadingStatus.set('idle');
          },
        });
      case 'dataSources':
        return this.searchService.searchDataSources({ q, page }).subscribe({
          next: (res) => {
            this.dataSources.update(state => ({
              ...state,
              content: page === 0 ? res.content : [...state.content, ...res.content],
            }))
            this.loadingStatus.set('idle');
            this.showPopup.set(true);
          },
          error: (err) => {
            console.error(err);
            this.loadingStatus.set('idle');
            this.showPopup.set(false);
          },
        });
      case 'facilities':
        return this.searchService.searchFacilities({ q, page }).subscribe({
          next: (res) => {
            this.facilities.update(state => ({
              ...state,
              content: page === 0 ? res.content : [...state.content, ...res.content],
            }))
            this.loadingStatus.set('idle');
            this.showPopup.set(true);
          },
          error: (err) => {
            console.error(err);
            this.loadingStatus.set('idle');
          },
        });
    }
  }

  private search({
    q,
    page,
  }: QueryParams): void | Subscription {
    this.loadingStatus.set('loading');
    return this.searchService.search({ q, page }).subscribe({
      next: (res) => {
        this.shipments.set(res.shipments);
        this.facilities.set(res.facilities);
        this.dataSources.set(res.dataSources);
        this.loadingStatus.set('idle');
        this.showPopup.set(true);
        this.scrollToTop();
      },
      error: (err) => {
        console.error(err);
        this.loadingStatus.set('idle');
      },
    });
  }

  private queryInputSubscription: Subscription | undefined;

  public ngOnInit(): void {
    this.queryInputSubscription =
      this.searchForm.controls.queryInput.valueChanges
        .pipe(
          debounceTime(300),
          filter((value) => {
            if (value.trim().length >= 3) {
              return true;
            }

            this.showPopup.set(false);
            return false;
          }),
        )
        .subscribe({
          next: (q) => {
            this.query.set({
              q: q.trim(),
              page: 0,
            });
            this.search({ q: q.trim(), page: 0 });
          },
        });
  }

  public ngOnDestroy(): void {
    this.queryInputSubscription?.unsubscribe();
  }

  private resetSearchState(): void {
    this.tab.set('shipments');
    this.shipments.set({
      content: [],
      totalElements: 0
    });
    this.dataSources.set({
      content: [],
      totalElements: 0
    });
    this.facilities.set({
      content: [],
      totalElements: 0
    });
    this.showPopup.set(false);
    this.searchForm.controls.queryInput.reset();
  }

  @HostListener('document:keydown', ['$event'])
  public keydown(event: KeyboardEvent): void {
    if (event.code === 'Escape') {
      this.resetSearchState();
    }
  }

  @HostListener('document:click', ['$event'])
  public documentClick(event: PointerEvent): void {
    if (!this.contains(event.target!)) {
      this.resetSearchState();
    }
  }

  public selectTab(key: SearchResultKey): void {
    this.tab.set(key);
    this.scrollToTop();
  }

  public handleLoadMore() {
    const currentPage = this.query().page;
    const nextPage = currentPage + 1;

    this.query.update(curr => ({
      ...curr,
      page: nextPage
    }));
    this.searchByTab({
      q: this.query().q,
      page: nextPage,
      tab: this.tab()
    });
  }

  public toggleSearch(): void {
    this.responsiveService.toggleSearch();
    this.resetSearchState();
  }

  public onKeydownEnter(event: Event): void {
    event.preventDefault();
  }

  private contains(target: EventTarget): boolean {
    const status = this.loadingStatus();
    if (status === 'loading' || status === 'loading-more') {
      return true;
    }
    return (
      this.searchElement.nativeElement.contains(target) ||
      (this.popup ? this.popup.nativeElement.contains(target) : false)
    );
  }

  private scrollToTop(): void {
    const inputPopup = this.popup?.nativeElement;

    if (inputPopup) {
      inputPopup.querySelector('main').scrollTop = 0
    }
  }
}

type QueryParams = { q: string; page: number };
